import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { createSelector } from 'reselect';
import type { FieldErrors, EntityErrors, EntityErrorState } from '../reducers/dealScreenErrorReducer';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { ValidationEntity } from '@makemydeal/dr-shared-ui-utils';
import { VPP_CONTRACT_API_ERRORS_KEY, VPP_GAP_CONTRACT_ERRORS_KEY, VPP_PRODUCT_ERRORS_KEY } from '../utils/vppProductErrors';

export type EntityKey = DealTabs | ValidationEntity;

export const getEntityErrorCount =
    (entity: EntityKey) =>
    (state: StateTree): number => {
        return state.dealScreenError?.[entity]?.errorCount || 0;
    };

export const getErrorCount = (state: StateTree): number => {
    if (!state.dealScreenError) return 0;
    let errorCount: number = 0;
    Object.keys(state.dealScreenError).forEach((entity) => {
        errorCount += state.dealScreenError[entity]?.errorCount || 0;
    });
    return errorCount;
};

export const getEntitiesWithErrorCounts = (state: StateTree): Record<EntityKey, number> => {
    if (!state.dealScreenError) return {} as Record<EntityKey, number>;
    const errorCounts: Record<EntityKey, number> = {} as Record<EntityKey, number>;
    Object.keys(state.dealScreenError).forEach((entity) => {
        const entityErrorCount = state.dealScreenError[entity]?.errorCount || 0;
        const entityRecommendationCount = state.dealScreenError[entity]?.recommendationCount || 0;
        if (state.dealScreenError[entity]?.visited) {
            errorCounts[entity as EntityKey] = entityErrorCount;
        } else {
            errorCounts[entity as EntityKey] = entityErrorCount + entityRecommendationCount;
        }
    });
    return errorCounts;
};

export const getEntityValidationErrors = createSelector(
    [(state: StateTree) => state.dealScreenError, (_state: StateTree, entity: EntityKey) => entity],
    (dealScreenError: { [entity: string]: EntityErrorState } | undefined, entity: EntityKey): EntityErrors => {
        return dealScreenError?.[entity]?.accumulatedErrors || {};
    }
);

export const getFieldValidationError =
    (entity: EntityKey, fieldName: string) =>
    (state: StateTree): boolean => {
        return state.dealScreenError[entity]?.accumulatedErrors[fieldName] || false;
    };

export const getCategoryValidationErrors = createSelector(
    [
        (state: StateTree, entity: EntityKey) => state.dealScreenError?.[entity],
        (_state: StateTree, _entity: EntityKey, category: string) => category
    ],
    (entityErrorState: EntityErrorState | undefined, category: string): Array<FieldErrors> => {
        const categoryValue = entityErrorState?.accumulatedErrors?.[category];
        return Array.isArray(categoryValue) ? categoryValue : [];
    }
);

const collectErrorKeys = (protectionErrors: Record<string, any> | undefined, namespace: string): Set<string> => {
    const productErrors = (protectionErrors?.[namespace] ?? {}) as Record<string, FieldErrors>;

    return new Set(
        Object.entries(productErrors)
            .filter(([, fieldErrors]) => Object.values(fieldErrors ?? {}).some(Boolean))
            .map(([key]) => key)
    );
};

const getProtectionErrors = (state: StateTree) =>
    state.dealScreenError?.[DealTabs.PROTECTION]?.errors as Record<string, any> | undefined;

/**
 * Keys of every VPP product carrying an error, from any writer. Rows match against
 * `getVppProductErrorKey` so each claims only its own.
 */
export const getVppProductErrorKeys = createSelector([getProtectionErrors], (protectionErrors): Set<string> => {
    return new Set([
        ...collectErrorKeys(protectionErrors, VPP_PRODUCT_ERRORS_KEY),
        ...collectErrorKeys(protectionErrors, VPP_GAP_CONTRACT_ERRORS_KEY),
        ...collectErrorKeys(protectionErrors, VPP_CONTRACT_API_ERRORS_KEY)
    ]);
});

/**
 * Save-flow product errors field by field, so a row can message the exact field Save Deal rejected.
 */
export const getVppProductFieldErrors = createSelector(
    [getProtectionErrors],
    (protectionErrors): Record<string, FieldErrors> =>
        (protectionErrors?.[VPP_PRODUCT_ERRORS_KEY] ?? {}) as Record<string, FieldErrors>
);

/** Contract-flow errors only, for the alert whose message is specific to contract validation. */
export const getGapContractErrorKeys = createSelector(
    [getProtectionErrors],
    (protectionErrors): Set<string> => collectErrorKeys(protectionErrors, VPP_GAP_CONTRACT_ERRORS_KEY)
);

/** Contract-API coverage-term errors only, for the same contract-validation alert treatment as GAP. */
export const getContractApiErroredVppErrorKeys = createSelector(
    [getProtectionErrors],
    (protectionErrors): Set<string> => collectErrorKeys(protectionErrors, VPP_CONTRACT_API_ERRORS_KEY)
);

export const getEntityAggregatedErrors = (state: RealState | any): Record<EntityKey, number> => {
    if (!state.__isScoped || !state.__scopeMap) {
        return getEntitiesWithErrorCounts(state as StateTree);
    }

    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const activeScopeIds = scopedStateSelectors.getActiveScopesOrder(state);
    const aggregatedEntityErrors: Record<EntityKey, number> = {} as Record<EntityKey, number>;

    for (const scopeId of activeScopeIds) {
        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree;
        if (scopeState && scopeState.dealScreenError) {
            const scopeEntityErrors = getEntitiesWithErrorCounts(scopeState);
            Object.entries(scopeEntityErrors).forEach(([entity, count]) => {
                const entityKey = entity as EntityKey;

                if (entityKey === DealTabs.TRADE_IN && scopeId !== primaryScopeId) {
                    return; // Skip non-primary scopes for Trade-In tab
                }

                aggregatedEntityErrors[entityKey] = (aggregatedEntityErrors[entityKey] || 0) + count;
            });
        }
    }

    return aggregatedEntityErrors;
};
